import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import {
    Receipt, ArrowRight, CheckCircle, XCircle, CurrencyInr, BellRinging,
    Trash, Plus, Calendar, ClockCountdown, Sparkle, Stethoscope, Wrench,
} from "@phosphor-icons/react";
import AdminManualInvoiceDialog from "@/components/admin/AdminManualInvoiceDialog";

/* iter98.99e0 — New lifecycle:
     outstanding → partially_paid → paid
     outstanding → overdue (auto-flip when due_at passes)
     outstanding → cancelled (admin cancels) */
const STATUS_TONE = {
    outstanding:    { label: "Outstanding",    cls: "bg-amber-50 text-amber-800 border-amber-200" },
    overdue:        { label: "Overdue",        cls: "bg-rose-50 text-rose-700 border-rose-200" },
    partially_paid: { label: "Partially paid", cls: "bg-amber-100 text-amber-900 border-amber-300" },
    paid:           { label: "Paid",           cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    cancelled:      { label: "Cancelled",      cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const ACTIVE_AR = new Set(["outstanding", "partially_paid", "overdue"]);
const EDITABLE = new Set(["outstanding", "partially_paid", "overdue"]);

/* Planning-mode badge — CoE (teal) vs Self-plan (amber) vs Manual (slate)
   so admin instantly knows which queue an invoice came from. */
const PLANNING_BADGE = {
    coe:    { label: "CoE",       cls: "bg-primary/10 text-primary border-primary/20",   Icon: Sparkle },
    doctor: { label: "Self-plan", cls: "bg-amber-50 text-amber-800 border-amber-200",    Icon: Stethoscope },
    manual: { label: "Manual",    cls: "bg-slate-100 text-slate-700 border-slate-300",   Icon: Wrench },
};

const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso) =>
    !iso ? "—" : new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

/* `due_at - now` expressed as "in 17 days" / "overdue by 4 days". */
const dueRelative = (dueAt) => {
    if (!dueAt) return null;
    const ms = new Date(dueAt).getTime() - Date.now();
    const days = Math.round(ms / 86400000);
    if (days > 1) return { label: `in ${days} days`, tone: days <= 7 ? "amber" : "neutral" };
    if (days === 1) return { label: "tomorrow", tone: "amber" };
    if (days === 0) return { label: "today", tone: "amber" };
    return { label: `overdue ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`, tone: "rose" };
};

export default function AdminBilling({ embedded = false }) {
    const [tab, setTab] = useState("all");
    /* iter98.99g (Phase 2) — second filter axis. Planning-mode filter
       is applied BEFORE the status bucketing so the status tabs +
       summary tiles drill down within the active mode. */
    const [mode, setMode] = useState("all");
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openInvoiceId, setOpenInvoiceId] = useState(null);
    const [manualOpen, setManualOpen] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/provider-invoices");
            setInvoices(r.data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, []);

    /* Bucket invoices by `case_planning_mode` for the planning-mode chip
       counts. Done on the full `invoices` set so the chip counts always
       reflect totals regardless of which mode is currently active. */
    const modeBuckets = useMemo(() => {
        const b = { coe: [], doctor: [], manual: [], other: [] };
        for (const i of invoices) {
            const m = i.case_planning_mode;
            if (m === "coe") b.coe.push(i);
            else if (m === "doctor") b.doctor.push(i);
            else if (m === "manual") b.manual.push(i);
            else b.other.push(i);
        }
        return b;
    }, [invoices]);

    /* Pick the active mode's row set (or all rows if mode = "all"). */
    const modeFiltered = useMemo(() => {
        if (mode === "all") return invoices;
        return modeBuckets[mode] || [];
    }, [mode, invoices, modeBuckets]);

    /* Bucket the mode-filtered invoices by status for the tab counts. */
    const buckets = useMemo(() => {
        const b = { outstanding: [], overdue: [], partially_paid: [], paid: [], cancelled: [] };
        for (const i of modeFiltered) {
            if (b[i.status]) b[i.status].push(i);
        }
        return b;
    }, [modeFiltered]);

    /* A/R metrics — outstanding $ + overdue $ + paid lifetime $. */
    const summary = useMemo(() => {
        const sumBalance = (rows) => rows.reduce((n, i) => n + ((i.total || 0) - (i.amount_paid || 0)), 0);
        const outAmt = sumBalance([...buckets.outstanding, ...buckets.partially_paid]);
        const overdueAmt = sumBalance(buckets.overdue);
        const paidAmt = buckets.paid.reduce((n, i) => n + (i.amount_paid || 0), 0);
        return { outAmt, overdueAmt, paidAmt };
    }, [buckets]);

    /* Pick the active-tab row set (within the active planning-mode filter). */
    const visible = useMemo(() => {
        if (tab === "all") return modeFiltered;
        return buckets[tab] || [];
    }, [tab, modeFiltered, buckets]);

    const openInvoice = invoices.find((i) => i.id === openInvoiceId);

    return (
        <div data-testid="admin-billing-page" className="space-y-6">
            {/* HEADER — suppressed when embedded inside the Finance hub. */}
            {!embedded && (
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="font-display text-3xl font-medium tracking-tight">B2B Billing</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Invoices issued by Invisique™ HQ to provider clinics. Auto-generated on doctor approval (CoE-Plan + Self-Plan) — payable within 30 days.
                        </p>
                    </div>
                    <Button
                        onClick={() => setManualOpen(true)}
                        className="rounded-full"
                        data-testid="admin-billing-create-invoice"
                    >
                        <Plus size={14} weight="bold" className="mr-1" /> Create invoice
                    </Button>
                </div>
            )}
            {embedded && (
                <div className="flex justify-end">
                    <Button
                        onClick={() => setManualOpen(true)}
                        size="sm"
                        className="rounded-full"
                        data-testid="admin-billing-create-invoice"
                    >
                        <Plus size={12} weight="bold" className="mr-1" /> Create invoice
                    </Button>
                </div>
            )}

            {/* SUMMARY TILES */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-billing-summary">
                <SummaryTile
                    label="Outstanding"
                    value={formatINR(summary.outAmt)}
                    sub={`${buckets.outstanding.length + buckets.partially_paid.length} unpaid`}
                    tone={summary.outAmt > 0 ? "amber" : "neutral"}
                />
                <SummaryTile
                    label="Overdue"
                    value={formatINR(summary.overdueAmt)}
                    sub={`${buckets.overdue.length} past due`}
                    tone={summary.overdueAmt > 0 ? "rose" : "neutral"}
                />
                <SummaryTile
                    label="Paid (lifetime)"
                    value={formatINR(summary.paidAmt)}
                    sub={`${buckets.paid.length} settled`}
                    tone="emerald"
                />
                <SummaryTile
                    label="Active invoices"
                    value={(buckets.outstanding.length + buckets.partially_paid.length + buckets.overdue.length).toString()}
                    sub="In play"
                    tone="primary"
                />
            </div>

            {/* PLANNING-MODE FILTER CHIPS — iter98.99g (Phase 2).
                Drill-down by which queue auto-generated the invoice:
                  • All       — every invoice regardless of case origin
                  • CoE-Plan  — invoices auto-generated from CoE-planned cases
                  • Self-Plan — invoices auto-generated from Self-Plan QA flow
                  • Other     — manual invoices / legacy rows without a mode
                The chips sit ABOVE the status tabs so the user picks
                "which queue" first, then drills into status within. */}
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-billing-mode-filter">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1">Queue:</span>
                {[
                    { key: "all",    label: "All",       count: invoices.length },
                    { key: "coe",    label: "CoE-Plan",  count: modeBuckets.coe.length },
                    { key: "doctor", label: "Self-Plan", count: modeBuckets.doctor.length },
                    { key: "manual", label: "Manual",    count: modeBuckets.manual.length },
                    { key: "other",  label: "Other",     count: modeBuckets.other.length },
                ].map((m) => {
                    const active = mode === m.key;
                    return (
                        <button
                            key={m.key}
                            onClick={() => { setMode(m.key); setTab("all"); }}
                            data-testid={`admin-billing-mode-${m.key}`}
                            className={`px-3 py-1 text-[11px] font-semibold rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                                active
                                    ? "bg-primary/10 border-primary/40 text-primary"
                                    : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                            }`}
                        >
                            {m.label}
                            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0 ${
                                active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                            }`}>{m.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* TABS — canonical AdminSegmentedTabs (iter101.32) */}
            <AdminSegmentedTabs
                testid="admin-billing-tabs"
                tabTestidBase="admin-billing-tab"
                active={tab}
                onChange={setTab}
                showZero
                tabs={[
                    { id: "all",            label: "All",             count: invoices.length },
                    { id: "outstanding",    label: "Outstanding",     count: buckets.outstanding.length,    tone: "warning" },
                    { id: "overdue",        label: "Overdue",         count: buckets.overdue.length,        tone: "danger" },
                    { id: "partially_paid", label: "Partially paid",  count: buckets.partially_paid.length, tone: "warning" },
                    { id: "paid",           label: "Paid",            count: buckets.paid.length },
                    { id: "cancelled",      label: "Cancelled",       count: buckets.cancelled.length },
                ]}
            />

            {/* CONTENT */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-xs text-muted-foreground">Loading…</div>
                ) : (
                    <InvoiceTable
                        items={visible}
                        onOpen={(id) => setOpenInvoiceId(id)}
                        onRemind={async (id) => {
                            try {
                                await api.post(`/admin/provider-invoices/${id}/send-reminder`);
                                toast.success("Reminder logged · channels will dispatch when wired (email/SMS/WhatsApp).");
                                reload();
                            } catch (err) {
                                toast.error(err.response?.data?.detail || "Failed to log reminder");
                            }
                        }}
                        empty={
                            tab === "all"            ? "No B2B invoices yet — they auto-generate the moment a doctor approves a plan (CoE or Self-Plan)." :
                            tab === "outstanding"    ? "No outstanding invoices." :
                            tab === "overdue"        ? "No overdue invoices — well done." :
                            tab === "partially_paid" ? "No partially paid invoices." :
                            tab === "paid"           ? "No paid invoices yet." :
                                                       "No cancelled invoices."
                        }
                    />
                )}
            </div>

            <AdminInvoiceDialog
                invoice={openInvoice}
                onClose={() => setOpenInvoiceId(null)}
                onChanged={reload}
            />

            <AdminManualInvoiceDialog
                open={manualOpen}
                onOpenChange={setManualOpen}
                onCreated={reload}
            />
        </div>
    );
}

/* ─────────────────── Summary tile ─────────────────── */

function SummaryTile({ label, value, sub, tone }) {
    const toneCls = {
        amber:   "bg-amber-50 border-amber-200",
        emerald: "bg-emerald-50 border-emerald-200",
        rose:    "bg-rose-50 border-rose-200",
        primary: "bg-primary/5 border-primary/20",
        neutral: "bg-card border-border",
    }[tone] || "bg-card border-border";
    return (
        <div className={`rounded-xl border p-4 ${toneCls}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="font-display text-2xl font-bold mt-1 tabular-nums">{value}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
        </div>
    );
}

/* ─────────────────── Invoice list table ─────────────────── */

function InvoiceTable({ items, onOpen, onRemind, empty }) {
    if (items.length === 0) {
        return (
            <div className="p-12 text-center text-muted-foreground" data-testid="invoice-table-empty">
                <Receipt size={20} weight="duotone" className="mx-auto text-muted-foreground/60 mb-2" />
                <p className="text-sm">{empty}</p>
            </div>
        );
    }
    return (
        <div className="divide-y divide-border/40" data-testid="invoice-table">
            {items.map((inv) => {
                const tone = STATUS_TONE[inv.status] || STATUS_TONE.outstanding;
                const balance = (inv.total || 0) - (inv.amount_paid || 0);
                const planning = PLANNING_BADGE[inv.case_planning_mode] || PLANNING_BADGE.coe;
                const PlanningIcon = planning.Icon;
                const due = dueRelative(inv.due_at);
                const canRemind = ACTIVE_AR.has(inv.status);
                return (
                    <div
                        key={inv.id}
                        data-testid={`admin-inv-row-${inv.id}`}
                        className="group w-full px-5 py-3.5 flex items-center gap-4 hover:bg-secondary/30 transition-colors"
                    >
                        <button
                            onClick={() => onOpen(inv.id)}
                            className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 hover:bg-primary/20"
                            data-testid={`admin-inv-icon-${inv.id}`}
                            aria-label="Open invoice"
                        >
                            <Receipt size={16} weight="duotone" className="text-primary" />
                        </button>
                        <button
                            onClick={() => onOpen(inv.id)}
                            className="flex-1 min-w-0 text-left"
                            data-testid={`admin-inv-main-${inv.id}`}
                        >
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs font-bold">{inv.code}</span>
                                <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-0.5 ${tone.cls}`}>
                                    {tone.label}
                                </span>
                                <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-0.5 inline-flex items-center gap-1 ${planning.cls}`}>
                                    <PlanningIcon size={10} weight="fill" /> {planning.label}
                                </span>
                                {due && (
                                    <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${
                                        due.tone === "rose" ? "text-rose-700 bg-rose-50" :
                                        due.tone === "amber" ? "text-amber-800 bg-amber-50" :
                                                               "text-muted-foreground bg-muted/50"
                                    }`}>
                                        {due.tone === "rose" ? <ClockCountdown size={10} weight="bold" /> : <Calendar size={10} weight="bold" />}
                                        Due {due.label}
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground mt-0.5 truncate">
                                {inv.doctor_name || inv.clinic_name || "—"} · {(inv.case_type || "—").replace(/_/g, " ")}
                                {inv.patient_name && ` · ${inv.patient_name}`}
                            </div>
                        </button>
                        <div className="text-right shrink-0">
                            <div className="text-sm font-semibold tabular-nums">{formatINR(inv.total)}</div>
                            {balance > 0 && inv.status !== "cancelled" && (
                                <div className="text-[11px] text-rose-700 font-medium tabular-nums">{formatINR(balance)} due</div>
                            )}
                        </div>
                        {canRemind && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); onRemind(inv.id); }}
                                data-testid={`send-reminder-${inv.id}`}
                                className="rounded-full h-8 px-3 text-xs"
                                title="Send payment reminder"
                            >
                                <BellRinging size={12} weight="duotone" className="mr-1" />
                                Remind
                            </Button>
                        )}
                        <ArrowRight size={14} className="text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </div>
                );
            })}
        </div>
    );
}

/* ─────────────────── Admin invoice dialog (full editor) ─────────────────── */

/* Stable client-side key for line-item React reconciliation. */
let _adminLiSeq = 0;
const newAdminLiKey = () => `ali-${++_adminLiSeq}`;

function AdminInvoiceDialog({ invoice, onClose, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [lineItems, setLineItems] = useState([]);
    const [taxPct, setTaxPct] = useState(0);
    const [notes, setNotes] = useState("");
    const [dueAt, setDueAt] = useState("");
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
    const [paymentRef, setPaymentRef] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (invoice) {
            setLineItems((invoice.line_items || []).map((li) => ({ ...li, _key: newAdminLiKey() })));
            setTaxPct(invoice.tax_pct || 0);
            setNotes(invoice.notes || "");
            setDueAt(invoice.due_at ? invoice.due_at.slice(0, 10) : "");
            setEditing(false);
            setPaymentAmount("");
            setPaymentRef("");
        }
    }, [invoice?.id]);

    if (!invoice) return null;
    const tone = STATUS_TONE[invoice.status] || STATUS_TONE.outstanding;
    const planning = PLANNING_BADGE[invoice.case_planning_mode] || PLANNING_BADGE.coe;
    const PlanningIcon = planning.Icon;
    const balance = (invoice.total || 0) - (invoice.amount_paid || 0);
    const canEdit = EDITABLE.has(invoice.status);
    const canPay = ACTIVE_AR.has(invoice.status) && balance > 0;
    const canRemind = ACTIVE_AR.has(invoice.status);
    const canCancel = invoice.status !== "cancelled" && invoice.status !== "paid";

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/provider-invoices/${invoice.id}`, {
                line_items: lineItems.map(({ _key, ...rest }) => rest),
                tax_pct: parseFloat(taxPct) || 0,
                notes,
                due_at: dueAt ? new Date(dueAt + "T23:59:59Z").toISOString() : undefined,
            });
            toast.success("Invoice updated");
            setEditing(false);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const sendReminder = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/provider-invoices/${invoice.id}/send-reminder`);
            toast.success("Reminder logged · channels will dispatch when wired (email/SMS/WhatsApp).");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reminder failed");
        } finally {
            setBusy(false);
        }
    };

    const recordPayment = async () => {
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            toast.error("Enter a positive amount");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/provider-invoices/${invoice.id}/payments`, {
                amount: parseFloat(paymentAmount),
                method: paymentMethod,
                reference: paymentRef,
            });
            toast.success("Payment recorded");
            setPaymentAmount("");
            setPaymentRef("");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to record payment");
        } finally {
            setBusy(false);
        }
    };

    const cancelInv = async () => {
        if (!await confirmDialog({
            title: "Cancel this invoice?",
            message: "This stops A/R accrual and hides the invoice from the doctor. The record stays in the DB for audit.",
            confirmText: "Cancel invoice",
            tone: "danger",
        })) return;
        setBusy(true);
        try {
            await api.post(`/admin/provider-invoices/${invoice.id}/cancel`);
            toast.success("Invoice cancelled");
            onChanged?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        } finally {
            setBusy(false);
        }
    };

    const setLi = (idx, key, val) => {
        const next = [...lineItems];
        next[idx] = { ...next[idx], [key]: val };
        setLineItems(next);
    };
    const addLi = () => setLineItems([...lineItems, { _key: newAdminLiKey(), description: "", quantity: 1, unit_price: 0 }]);
    const removeLi = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));

    return (
        <Dialog open={!!invoice} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <DialogTitle className="font-mono">{invoice.code}</DialogTitle>
                            <DialogDescription className="mt-1">
                                {invoice.doctor_name || invoice.clinic_name} · {(invoice.case_type || "—").replace(/_/g, " ")} · {invoice.patient_name || "—"}
                            </DialogDescription>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-1 ${tone.cls}`}>
                                {tone.label}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-0.5 inline-flex items-center gap-1 ${planning.cls}`}>
                                <PlanningIcon size={10} weight="fill" /> {planning.label}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-4 text-[11px] text-muted-foreground mt-2">
                        <span><strong>Issued:</strong> {fmtDate(invoice.issued_at)}</span>
                        <span><strong>Due:</strong> {fmtDate(invoice.due_at)}</span>
                        {invoice.paid_at && <span><strong>Paid:</strong> {fmtDate(invoice.paid_at)}</span>}
                    </div>
                </DialogHeader>

                {/* LINE ITEMS */}
                <div className="space-y-2 py-2">
                    <div className="flex items-center justify-between">
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Line items</div>
                        {canEdit && !editing && (
                            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="edit-line-items">Edit</Button>
                        )}
                    </div>
                    {editing ? (
                        <>
                            {lineItems.map((li, idx) => (
                                <div key={li._key} className="grid grid-cols-[1fr_60px_90px_24px] gap-2 items-center">
                                    <Input
                                        value={li.description}
                                        onChange={(e) => setLi(idx, "description", e.target.value)}
                                        placeholder="Description"
                                        className="h-8 text-sm"
                                    />
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={li.quantity}
                                        onChange={(e) => setLi(idx, "quantity", parseFloat(e.target.value) || 0)}
                                        className="h-8 text-sm"
                                    />
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={li.unit_price}
                                        onChange={(e) => setLi(idx, "unit_price", parseFloat(e.target.value) || 0)}
                                        className="h-8 text-sm"
                                    />
                                    <button onClick={() => removeLi(idx)} className="text-muted-foreground hover:text-rose-600">
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                            <Button size="sm" variant="outline" onClick={addLi} data-testid="add-line-item" className="mt-1">
                                <Plus size={12} className="mr-1" /> Add line
                            </Button>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <div>
                                    <Label className="text-xs font-semibold">Tax %</Label>
                                    <Input type="number" min="0" max="100" step="0.01" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold">Due date</Label>
                                    <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className="h-8 text-sm" data-testid="due-at-input" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Notes</Label>
                                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-sm" />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button onClick={save} disabled={busy} className="rounded-full" data-testid="save-line-items">Save changes</Button>
                                <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
                            </div>
                        </>
                    ) : (
                        <>
                            {(invoice.line_items || []).map((li, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                    <div className="text-foreground">{li.description} <span className="text-muted-foreground">× {li.quantity}</span></div>
                                    <div className="text-foreground font-medium tabular-nums">{formatINR(li.line_total)}</div>
                                </div>
                            ))}
                            <div className="border-t border-border pt-2 mt-2 space-y-1 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{formatINR(invoice.subtotal)}</span></div>
                                {invoice.tax_amount > 0 && (
                                    <div className="flex justify-between"><span className="text-muted-foreground">Tax ({invoice.tax_pct}%)</span><span className="tabular-nums">{formatINR(invoice.tax_amount)}</span></div>
                                )}
                                <div className="flex justify-between font-bold pt-1 border-t border-border"><span>Total</span><span className="tabular-nums">{formatINR(invoice.total)}</span></div>
                                <div className="flex justify-between text-emerald-700"><span>Paid</span><span className="tabular-nums">{formatINR(invoice.amount_paid)}</span></div>
                                <div className={`flex justify-between font-bold ${balance > 0 ? "text-rose-700" : "text-emerald-700"}`}><span>Balance</span><span className="tabular-nums">{formatINR(balance)}</span></div>
                            </div>
                            {invoice.notes && <div className="text-xs text-muted-foreground italic mt-2">{invoice.notes}</div>}
                        </>
                    )}
                </div>

                {/* PAYMENT RECORDING (Admin only) */}
                {canPay && !editing && (
                    <div className="border-t border-border pt-3 mt-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Record payment</div>
                        <div className="grid grid-cols-[1fr_140px_1fr] gap-2">
                            <Input
                                type="number" min="0" step="0.01"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder={`Up to ${formatINR(balance)}`}
                                className="h-9"
                                data-testid="payment-amount"
                            />
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="h-9 px-2 rounded-md border border-border bg-card text-sm"
                                data-testid="payment-method"
                            >
                                <option value="bank_transfer">Bank transfer</option>
                                <option value="upi">UPI</option>
                                <option value="card">Card</option>
                                <option value="cheque">Cheque</option>
                                <option value="other">Other</option>
                            </select>
                            <Input
                                value={paymentRef}
                                onChange={(e) => setPaymentRef(e.target.value)}
                                placeholder="UTR / reference (optional)"
                                className="h-9"
                                data-testid="payment-reference"
                            />
                        </div>
                        <Button onClick={recordPayment} disabled={busy || !paymentAmount} className="rounded-full mt-2" data-testid="record-payment">
                            <CurrencyInr size={14} className="mr-1" /> Record payment
                        </Button>
                    </div>
                )}

                {/* PAYMENT HISTORY */}
                {(invoice.payments || []).length > 0 && (
                    <div className="border-t border-border pt-3 mt-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Payment history</div>
                        <div className="space-y-1">
                            {(invoice.payments || []).map((p) => (
                                <div key={p.id} className="flex justify-between text-xs">
                                    <div>
                                        <span className="tabular-nums">{p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</span>
                                        <span className="text-muted-foreground ml-2">{(p.method || "").replace(/_/g, " ")}</span>
                                        {p.reference && <span className="text-muted-foreground ml-2 font-mono">{p.reference}</span>}
                                    </div>
                                    <span className="font-semibold tabular-nums">{formatINR(p.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* REMINDER HISTORY */}
                {(invoice.reminders || []).length > 0 && (
                    <div className="border-t border-border pt-3 mt-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
                            <BellRinging size={11} weight="duotone" /> Reminder history ({invoice.reminders.length})
                        </div>
                        <div className="space-y-1">
                            {(invoice.reminders || []).map((r) => (
                                <div key={r.id} className="flex justify-between text-[11px]">
                                    <span className="text-muted-foreground tabular-nums">
                                        {fmtDate(r.sent_at)} · {r.sent_by_name || "admin"}
                                    </span>
                                    <span className="text-muted-foreground">
                                        {r.email_sent ? "email " : ""}
                                        {r.sms_sent ? "sms " : ""}
                                        {r.whatsapp_sent ? "whatsapp " : ""}
                                        {!r.email_sent && !r.sms_sent && !r.whatsapp_sent && "logged"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 mt-2 flex-wrap">
                    {canCancel && !editing && (
                        <Button variant="ghost" onClick={cancelInv} disabled={busy} className="text-rose-600 hover:bg-rose-50" data-testid="cancel-invoice">
                            <XCircle size={14} className="mr-1" /> Cancel invoice
                        </Button>
                    )}
                    {canRemind && !editing && (
                        <Button onClick={sendReminder} disabled={busy} variant="outline" className="rounded-full" data-testid="send-reminder-dialog">
                            <BellRinging size={14} className="mr-1" weight="duotone" /> Send reminder
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
